import { Extension } from '@tiptap/core';
import Suggestion from '@tiptap/suggestion';
import { ReactRenderer } from '@tiptap/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import {
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  FileText,
  BookOpen,
  HelpCircle,
  Table,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import i18n from '../../i18n';
import { FAQ_BLOCK_CONTENT } from './FaqBlock';
import { CALLOUT_TYPES } from './Callout';

export interface SlashCommandItem {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  command: (props: { editor: any; range: any }) => void;
}

// This will be set by the editor component
let insertReferenceCallback: (() => void) | null = null;

// eslint-disable-next-line react-refresh/only-export-components
export const setInsertReferenceCallback = (callback: () => void) => {
  insertReferenceCallback = callback;
};

const calloutTypeLabel = (type: string): string => {
  switch (type) {
    case 'info': return i18n.t('editor.callout.typeInfo');
    case 'tip': return i18n.t('editor.callout.typeTip');
    case 'warning': return i18n.t('editor.callout.typeWarning');
    case 'danger': return i18n.t('editor.callout.typeDanger');
    default: return type;
  }
};

const getSuggestionItems = ({ query }: { query: string }): SlashCommandItem[] => {
  const items: SlashCommandItem[] = [
    {
      title: i18n.t('editor.slashCommand.referenceBlockTitle'),
      description: i18n.t('editor.slashCommand.referenceBlockDesc'),
      icon: BookOpen,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .run();
        if (insertReferenceCallback) {
          insertReferenceCallback();
        }
      },
    },
    {
      title: i18n.t('editor.slashCommand.faqTitle'),
      description: i18n.t('editor.slashCommand.faqDesc'),
      icon: HelpCircle,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertContent(FAQ_BLOCK_CONTENT)
          .run();
      },
    },
    {
      title: i18n.t('editor.slashCommand.tableTitle'),
      description: i18n.t('editor.slashCommand.tableDesc'),
      icon: Table,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
          .run();
      },
    },
    ...CALLOUT_TYPES.map((callout) => ({
      title: i18n.t('editor.slashCommand.calloutTitle', { label: calloutTypeLabel(callout.type) }),
      description: i18n.t('editor.slashCommand.calloutDesc', { label: calloutTypeLabel(callout.type).toLowerCase() }),
      icon: callout.icon,
      command: ({ editor, range }: { editor: any; range: any }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setCallout({ type: callout.type })
          .run();
      },
    })),
    {
      title: i18n.t('editor.slashCommand.heading1Title'),
      description: i18n.t('editor.slashCommand.heading1Desc'),
      icon: Heading1,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setNode('heading', { level: 1 })
          .run();
      },
    },
    {
      title: i18n.t('editor.slashCommand.heading2Title'),
      description: i18n.t('editor.slashCommand.heading2Desc'),
      icon: Heading2,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setNode('heading', { level: 2 })
          .run();
      },
    },
    {
      title: i18n.t('editor.slashCommand.heading3Title'),
      description: i18n.t('editor.slashCommand.heading3Desc'),
      icon: Heading3,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setNode('heading', { level: 3 })
          .run();
      },
    },
    {
      title: i18n.t('editor.slashCommand.bulletListTitle'),
      description: i18n.t('editor.slashCommand.bulletListDesc'),
      icon: List,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .toggleBulletList()
          .run();
      },
    },
    {
      title: i18n.t('editor.slashCommand.numberedListTitle'),
      description: i18n.t('editor.slashCommand.numberedListDesc'),
      icon: ListOrdered,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .toggleOrderedList()
          .run();
      },
    },
    {
      title: i18n.t('editor.slashCommand.quoteTitle'),
      description: i18n.t('editor.slashCommand.quoteDesc'),
      icon: Quote,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .toggleBlockquote()
          .run();
      },
    },
    {
      title: i18n.t('editor.slashCommand.codeBlockTitle'),
      description: i18n.t('editor.slashCommand.codeBlockDesc'),
      icon: Code,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .toggleCodeBlock()
          .run();
      },
    },
    {
      title: i18n.t('editor.slashCommand.paragraphTitle'),
      description: i18n.t('editor.slashCommand.paragraphDesc'),
      icon: FileText,
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setParagraph()
          .run();
      },
    },
  ];

  return items.filter(item =>
    item.title.toLowerCase().includes(query.toLowerCase()) ||
    item.description.toLowerCase().includes(query.toLowerCase())
  );
};

interface SlashCommandListProps {
  items: SlashCommandItem[];
  command: (item: SlashCommandItem) => void;
}

interface SlashCommandListContainerProps extends SlashCommandListProps {
  clientRect: (() => DOMRect | null) | null;
}

/** Imperative handle exposed to the Suggestion plugin for keyboard navigation. */
export interface SlashCommandListHandle {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const SlashCommandList = React.forwardRef<SlashCommandListHandle, SlashCommandListProps>(
  ({ items, command }, ref) => {
    const { t } = useTranslation();
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const itemRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

    // The Suggestion plugin passes a freshly filtered list on every keystroke;
    // start from the top again whenever it changes.
    React.useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    // Keep the highlighted item in view while arrowing through a long list.
    React.useEffect(() => {
      itemRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex]);

    React.useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (!items.length) {
          return false;
        }
        if (event.key === 'ArrowUp') {
          setSelectedIndex((index) => (index + items.length - 1) % items.length);
          return true;
        }
        if (event.key === 'ArrowDown') {
          setSelectedIndex((index) => (index + 1) % items.length);
          return true;
        }
        if (event.key === 'Enter') {
          const item = items[selectedIndex];
          if (item) {
            command(item);
            return true;
          }
        }
        return false;
      },
    }));

    return (
      <div className="z-50 min-w-[280px] max-h-[320px] overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md outline-none animate-in fade-in-0 zoom-in-95">
        {items.length ? (
          items.map((item, index) => (
            <button
              key={item.title}
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
              className={cn(
                "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-left outline-none transition-colors",
                "hover:bg-accent hover:text-accent-foreground",
                "focus:bg-accent focus:text-accent-foreground",
                index === selectedIndex && "bg-accent text-accent-foreground"
              )}
              onMouseEnter={() => setSelectedIndex(index)}
              onClick={() => command(item)}
              type="button"
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="font-medium truncate">{item.title}</span>
                <span className="text-xs text-muted-foreground truncate">{item.description}</span>
              </div>
            </button>
          ))
        ) : (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">{t('editor.slashCommand.noResults')}</div>
        )}
      </div>
    );
  }
);

SlashCommandList.displayName = 'SlashCommandList';

const SlashCommandListContainer = React.forwardRef<SlashCommandListHandle, SlashCommandListContainerProps>(
  ({ items, command, clientRect }, ref) => {
    const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null);

    React.useEffect(() => {
      if (!clientRect) return;

      const updatePosition = () => {
        const rect = clientRect();
        if (rect) {
          setPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
          });
        }
      };

      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }, [clientRect]);

    if (!position) return null;

    const content = (
      <div
        style={{
          position: 'absolute',
          top: `${position.top}px`,
          left: `${position.left}px`,
          zIndex: 9999,
        }}
      >
        <SlashCommandList ref={ref} items={items} command={command} />
      </div>
    );

    return createPortal(content, document.body);
  }
);

SlashCommandListContainer.displayName = 'SlashCommandListContainer';

export const SlashCommand = Extension.create({
  name: 'slashCommand',

  addOptions() {
    return {
      suggestion: {
        char: '/',
        command: ({ editor, range, props }: any) => {
          props.command({ editor, range });
        },
        items: getSuggestionItems,
        render: () => {
          let component: ReactRenderer<SlashCommandListHandle, SlashCommandListContainerProps> | null = null;

          return {
            onStart: (props: any) => {
              component = new ReactRenderer<SlashCommandListHandle, SlashCommandListContainerProps>(
                SlashCommandListContainer,
                {
                  props,
                  editor: props.editor,
                }
              );
            },

            onUpdate(props: any) {
              component?.updateProps(props);
            },

            onKeyDown(props: any) {
              if (props.event.key === 'Escape') {
                component?.destroy();
                component = null;
                return true;
              }

              // ReactRenderer forwards the component's imperative handle as `ref`.
              return component?.ref?.onKeyDown(props) ?? false;
            },

            onExit() {
              component?.destroy();
              component = null;
            },
          };
        },
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});
